import api from "@shared/api/client";
import { MAX_PHOTO_BYTES } from "@shared/constants/meter";
import { MeterConfirmAction, MeterSubmissionKind } from "@shared/types/meter";
import type { MeterExtractionResponse, MeterProviderInfo } from "@shared/types/meter";
import { extractErrorMessage, fileToBase64, findMissingMeterSettings } from "@shared/utils";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MeterCaptureActions, MeterCaptureInput, MeterCaptureState } from "./types";

const useMeterCapture = (
  provider: MeterProviderInfo | null,
  input: MeterCaptureInput,
): { capture: MeterCaptureState; actions: MeterCaptureActions } => {
  const { t } = useTranslation(["common", "meterReadings"]);
  const [extraction, setExtraction] = useState<MeterExtractionResponse | null>(null);
  const [readingValue, setReadingValue] = useState("");
  const [secondaryReadingValue, setSecondaryReadingValue] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const resetFlow = useCallback(() => {
    setExtraction(null);
    setReadingValue("");
    setSecondaryReadingValue("");
    setPhotoDataUrl(null);
    setError("");
  }, []);

  const selectFile = useCallback(
    async (file: File | undefined): Promise<void> => {
      if (!file || !provider) return;
      if (file.size > MAX_PHOTO_BYTES) {
        setError(t("meterReadings:photoTooLarge"));
        return;
      }

      setError("");
      setSuccessMessage("");
      setExtracting(true);
      setExtraction(null);
      setPhotoDataUrl(null);

      try {
        const base64 = await fileToBase64(file);
        setPhotoDataUrl(base64);
        const result = await api.meterReadings.extract({
          providerId: provider.id,
          photo: base64,
          mimeType: file.type || "image/jpeg",
        });
        setExtraction(result);
        setReadingValue(result.readingValue);
        setSecondaryReadingValue(result.secondaryReadingValue ?? "");
      } catch (err: unknown) {
        setError(extractErrorMessage(err, t("meterReadings:extractError")));
      } finally {
        setExtracting(false);
      }
    },
    [provider, t],
  );

  const confirm = useCallback(async (): Promise<void> => {
    if (!extraction || !readingValue.trim() || !provider) return;
    if (!input.settings) return;
    const missing = findMissingMeterSettings(input.settings, { submission: provider.submission });
    if (missing.length) {
      setError(t("meterReadings:settings.missingRequired"));
      return;
    }
    setConfirming(true);
    setError("");

    try {
      const result = await api.meterReadings.confirm({
        providerId: provider.id,
        id: extraction.id,
        readingValue: readingValue.trim(),
        secondaryReadingValue: provider.supportsSecondaryReading ? secondaryReadingValue.trim() || null : null,
      });
      setSuccessMessage(result.action === MeterConfirmAction.EMAIL ? t("meterReadings:successEmail") : t("meterReadings:successForm"));
      resetFlow();
      await input.onConfirmed();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t("meterReadings:confirmError")));
    } finally {
      setConfirming(false);
    }
  }, [extraction, readingValue, provider, input, secondaryReadingValue, resetFlow, t]);

  const capture: MeterCaptureState = {
    extraction,
    photoDataUrl,
    extracting,
    confirming,
    readingValue,
    secondaryReadingValue,
    error,
    successMessage,
    usesSecondaryReading: provider?.supportsSecondaryReading ?? false,
    usesEmail: provider?.submission === MeterSubmissionKind.EMAIL,
  };

  const actions: MeterCaptureActions = {
    selectFile,
    confirm,
    retake: resetFlow,
    clearSuccess: () => setSuccessMessage(""),
    changeReading: setReadingValue,
    changeSecondary: setSecondaryReadingValue,
  };

  return { capture, actions };
};

export { useMeterCapture };
